import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Edit, Trash2, Plus, Search, UserCheck, UserX, Shield, Crown, CheckCircle, XCircle, Phone } from "lucide-react";
import { useAdminData } from "@/hooks/useAdminData";
import UserActivationButton from "./UserActivationButton";
import AdminReactivateButton from "./AdminReactivateButton";
import UserActivationHistory from "./UserActivationHistory";
import WhatsAppContactButton from "./WhatsAppContactButton";
import WhatsAppFollowUpButton from "./WhatsAppFollowUpButton";
import ShareActivationButton from "./ShareActivationButton";

const AdminUsersList = () => {
  const { toast } = useToast();
  const { users, isLoading, updateUserPlan, updateUserRole, deleteUser, fetchUsers, confirmUserEmail } = useAdminData();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlan, setFilterPlan] = useState<string>("all");

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }).format(new Date(dateString));
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm("Tem certeza que deseja excluir este usuário? Esta ação é irreversível e removerá o usuário completamente do sistema.")) {
      await deleteUser(userId);
    }
  };

  const handleEditUser = (userId: string) => {
    toast({
      title: "Editar usuário",
      description: `Editando o usuário com ID: ${userId}`,
    });
    // Aqui implementaria a lógica de edição
  };

  const handleToggleAdminRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    await updateUserRole(userId, newRole);
  };

  const handleConfirmEmail = async (userId: string) => {
    if (confirm("Confirmar email deste usuário?")) {
      await confirmUserEmail(userId);
    }
  };

  const handleUpdatePlan = async (userId: string, newPlan: string) => {
    await updateUserPlan(userId, newPlan);
  };

  const filteredUsers = users.filter(user => {
    // Filtrar por termo de busca
    const matchesSearch = user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.whatsapp?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtrar por plano
    const matchesPlan = filterPlan === "all" || user.plan === filterPlan;
    
    return matchesSearch && matchesPlan;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Admin Reactivation Section */}
      <AdminReactivateButton />

      {/* Quick Activation Section */}
      <div className="bg-gradient-to-r from-gold-50 to-yellow-50 dark:from-gold-900/20 dark:to-yellow-900/20 border-2 border-gold-300 dark:border-gold-600 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-gold-800 dark:text-gold-200 mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5" />
          🚀 Ativação Rápida de Usuário
        </h3>
        <div className="flex flex-wrap gap-4">
          <UserActivationButton 
            userEmail="autismundo93@gmail.com" 
            plan="quarterly"
            onSuccess={() => fetchUsers()}
          />
        </div>
        <p className="text-sm text-gold-600 dark:text-gold-400 mt-3">
          <strong>Ação Solicitada:</strong> Ativar autismundo93@gmail.com no plano quarterly
        </p>
      </div>

      <div className="bg-gradient-to-r from-gold-50 to-yellow-50 dark:from-gold-900/20 dark:to-yellow-900/20 border-2 border-gold-300 dark:border-gold-600 rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-gold-800 dark:text-gold-200 mb-2 flex items-center gap-2">
          <Crown className="w-5 h-5" />
          👤 Painel de Controle de Usuários - Dados Reais
        </h3>
        <div className="text-sm text-gold-700 dark:text-gold-300 space-y-2">
          <p><strong>Total de Usuários:</strong> {users.length}</p>
          <p><strong>Usuários Premium/VIP:</strong> {users.filter(u => u.plan === 'premium' || u.plan === 'vip').length}</p>
          <p><strong>Administradores:</strong> {users.filter(u => u.role === 'admin').length}</p>
          <p><strong>Emails Confirmados:</strong> {users.filter(u => u.email_confirmed).length}</p>
          <p className="mt-3 text-center"><em>🏆 Controle total sobre usuários reais do sistema</em></p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar usuários (nome, email, WhatsApp)..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={filterPlan} onValueChange={setFilterPlan}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por plano" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os planos</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="basic">Básico</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Anual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="flex gap-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600">
          <Plus size={16} />
          <span>Novo Usuário</span>
        </Button>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>Data de Cadastro</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Email Confirmado</TableHead>
              <TableHead>Função</TableHead>
              <TableHead>Contato Rápido</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {user.role === "admin" && <Crown className="w-4 h-4 text-gold-500" />}
                      {user.first_name || 'Sem nome'}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.whatsapp ? (
                        <>
                          <Phone className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{user.whatsapp}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">Não informado</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={user.plan}
                      onValueChange={(newPlan) => handleUpdatePlan(user.id, newPlan)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue>
                          <Badge className={
                            user.plan === "premium" ? "bg-blue-500" :
                            user.plan === "vip" ? "bg-purple-500" :
                            user.plan === "basic" ? "bg-green-500" :
                            user.plan === "quarterly" ? "bg-gold-500" :
                            "bg-gray-500"
                          }>
                            {user.plan?.charAt(0).toUpperCase() + user.plan?.slice(1)}
                          </Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="basic">Básico (15 dias)</SelectItem>
                        <SelectItem value="premium">Premium (1 mês)</SelectItem>
                        <SelectItem value="vip">VIP (6 meses)</SelectItem>
                        <SelectItem value="quarterly">Quarterly (3 meses)</SelectItem>
                        <SelectItem value="annual">Anual (1 ano)</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    {user.subscription_end_date && new Date(user.subscription_end_date) < new Date() ? (
                      <Badge variant="destructive">Vencido</Badge>
                    ) : (
                      <Badge variant={user.is_subscribed ? "default" : "secondary"}>
                        {user.is_subscribed ? "Ativo" : "Inativo"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {user.email_confirmed ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Confirmado
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">
                            <XCircle className="w-3 h-3 mr-1" />
                            Pendente
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleConfirmEmail(user.id)}
                            className="text-xs"
                          >
                            Confirmar
                          </Button>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.role === "admin" ? "default" : "outline"} 
                           className={user.role === "admin" ? "bg-gold-500 text-black" : ""}>
                      {user.role === "admin" ? (
                        <div className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Admin
                        </div>
                      ) : "Usuário"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const isExpired = user.plan && user.plan !== "free" && user.subscription_end_date
                        && new Date(user.subscription_end_date) < new Date();
                      const isUnconfirmed = !user.email_confirmed;
                      const daysSinceCreated = user.created_at
                        ? Math.floor((Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24))
                        : 0;
                      const isCold = user.plan === "free" && user.email_confirmed && daysSinceCreated >= 7;
                      const showAny = isExpired || isUnconfirmed || isCold;
                      return (
                        <div className="flex gap-1 items-center">
                          {isExpired && <WhatsAppContactButton type="expired" user={user} />}
                          {isUnconfirmed && <WhatsAppContactButton type="unconfirmed" user={user} />}
                          {isCold && <WhatsAppContactButton type="cold" user={user} />}
                          <WhatsAppFollowUpButton user={user} />
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <UserActivationHistory
                        userId={user.id}
                        userName={user.first_name}
                        userEmail={user.email}
                        createdAt={user.created_at}
                        firstActivationDate={user.first_activation_date}
                        lastActivationDate={user.last_activation_date}
                        subscriptionEndDate={user.subscription_end_date}
                        activationCount={user.activation_count}
                      />
                      
                      <ShareActivationButton
                        userId={user.id}
                        userName={user.first_name}
                        userEmail={user.email}
                        userWhatsapp={user.whatsapp}
                        plan={user.plan}
                        subscriptionEndDate={user.subscription_end_date}
                      />

                      <Button size="sm" variant="ghost" onClick={() => handleEditUser(user.id)}>
                        <Edit size={16} />
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleToggleAdminRole(user.id, user.role)}
                        className={user.role === "admin" ? "text-red-500 hover:text-red-600" : "text-gold-500 hover:text-gold-600"}
                      >
                        {user.role === "admin" ? <UserX size={16} /> : <Shield size={16} />}
                      </Button>
                      
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteUser(user.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-4 text-muted-foreground">
                  Nenhum usuário encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">🎯 Controles de Administrador Supremo</h4>
        <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
          <p>• <strong>✏️ Editar:</strong> Modificar dados do usuário</p>
          <p>• <strong>📋 Plano:</strong> Alterar plano do usuário diretamente na tabela</p>
          <p>• <strong>👑 Admin:</strong> Conceder ou remover permissões de administrador</p>
          <p>• <strong>🗑️ Excluir:</strong> Remover usuário permanentemente do sistema (perfil + auth)</p>
          <p>• <strong>✅ Email Confirmado:</strong> Verde = confirmado, Vermelho = pendente com botão para confirmar</p>
          <p>• <strong>📱 WhatsApp:</strong> Número informado no cadastro</p>
          <p>• <strong>🚀 Ativação Rápida:</strong> Use o botão dourado acima para ativar usuários específicos</p>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersList;